import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/theme/utils'

const buttonVariants = cva(
  'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        warning:
          'bg-amber-500 text-amber-950 shadow-sm hover:bg-amber-600 focus-visible:ring-amber-600',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline: cn(
          'relative overflow-hidden border border-[#6686FF] bg-transparent text-foreground hover:text-foreground',
          'before:absolute before:inset-0 before:-z-[1] before:opacity-100',
          'before:bg-[radial-gradient(circle_at_top,rgba(102,134,255,0.2)_0%,rgba(0,0,0,0.5)_80%,rgba(102,134,255,0.1)_100%)]',
          '[box-shadow:0px_0px_6px_0px_#6686FF33,0px_0px_3px_0px_#6686FF22_inset]',
        ),
        outline2: cn(
          'relative overflow-hidden border border-[#3F455B] bg-transparent text-foreground hover:text-foreground',
          'before:absolute before:inset-0 before:-z-[1] before:opacity-100',
          'before:bg-[radial-gradient(circle_at_top,rgba(63,69,91,0.22)_50%,rgba(0,0,0,0.55)_80%,rgba(63,69,91,0.10)_100%)]',
          '[box-shadow:0px_0px_6px_0px_#3F455B33,0px_0px_3px_0px_#3F455B22_inset]',
        ),
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        white:
          'bg-white text-black shadow-sm hover:bg-white/90 rounded-full border border-black/20',
        ghost: 'hover:bg-accent hover:text-accent-foreground text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        card: 'bg-card text-card-foreground shadow-sm hover:bg-card/90 active:text-card-foreground/10',
      },
      size: {
        default: 'h-11 px-4 py-2',
        sm: 'h-9 px-3 text-xs',
        lg: 'h-12 px-8 typography-m3-body-medium',
        icon: 'h-9 w-9',
      },
      radius: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        default: 'rounded-lg',
        xl: 'rounded-xl',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      radius: 'default',
    },
  },
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const UiButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, radius, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, radius, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
UiButton.displayName = 'UiButton'

export { buttonVariants, UiButton }
